import { useAuth0 } from '@auth0/auth0-react'
import { isPlatform } from '@ionic/react'

import { BackButton } from 'components/BackButton'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import { ErrorView } from '../components/Error'
import { Footer } from '../components/Footer'
import { AGENCY_PLACEHOLDER_IMAGE, OrganizationLogo } from '../components/pageStatus/OrganizationLogo'
import { PageStatusSection, useMyPageEntry } from '../components/pageStatus/PageStatusSection'
import { VerifiedPageCard } from '../components/pageStatus/VerifiedPageCard'
import { ProgressBar } from '../components/ProgressBar'
import { Spinner } from '../components/Spinner'
import { StarRating } from '../components/StarRating'
import { TopLogo } from '../components/TopLogo'
import { useAppToast } from '../hooks/useAppToast'
import { shouldShowVerifiedBadge, usePageViewerRole } from '../hooks/usePageViewerRole'
import { Agency } from '../models/Agency.model'
import { FormSubmissionSummaryDTO } from '../models/FormSubmissionSummaryDTO.model'
import { useAuthToken } from '../providers/AuthStateContext'
import { AgencyService } from '../services/AgencyService/AgencyService'
import { navigateBackToDirectory } from '../utils/directorySearchUrl'
import { formatRating } from '../utils/formatRating'
import { applyScrollToReviewFromLocationState, DetailPageLocationState } from '../utils/reviewSuccessNavigation'

//-----------------------------------------------------------------------------
// SUB COMPONENTS
//-----------------------------------------------------------------------------

const formatAgencyLocation = (address: string | null): string | null => {
  if (!address) return null
  const parts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length >= 2) {
    return parts.slice(-2).join(', ')
  }
  return null
}

const formatAgencyWebsiteLabel = (website: string): string => {
  return website.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

type AgencySummaryItemProps = {
  summary: {
    average: number
    name: string
  }
}
const AgencySummaryItem = ({ summary }: AgencySummaryItemProps) => {
  // Format category name to match design (e.g., "Safety/Best Practices" -> "Safety best practices")
  const formatCategoryName = (name: string): string => {
    return name
      .replace(/\//g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-base font-medium text-white">{formatCategoryName(summary.name)}</span>
        <span className="text-base font-medium text-white">
          {formatRating(typeof summary.average === 'number' ? summary.average : Number(summary.average || 0))} / 5
        </span>
      </div>
      <ProgressBar value={summary.average} max={5} />
    </div>
  )
}

type ReviewItemProps = {
  review: FormSubmissionSummaryDTO
}
const AgencyReviewItem = ({ review }: ReviewItemProps) => {
  return (
    <li
      id={`review-${review.id}`}
      className="mb-6 bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex flex-col">
        {/* Header: Date, Position/Title, and Rating */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-500 mb-1">
              {dayjs(review.createdAt).format('MMMM D, YYYY')}
            </div>
            <div className="text-base font-semibold text-graphite-grey mb-2">
              <span dangerouslySetInnerHTML={review.position ? { __html: review.position } : undefined}></span>
              {review.position && review.title && <span className="mx-2 text-gray-400">•</span>}
              <span dangerouslySetInnerHTML={review.title ? { __html: review.title } : undefined}></span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StarRating rating={review.averageRating} size="md" />
            <span className="text-base font-medium text-gray-600">
              {typeof review.averageRating === 'number'
                ? review.averageRating.toFixed(1)
                : Number(review.averageRating || 0).toFixed(1)}
            </span>
          </div>
        </div>

        {/* Review Content */}
        {review.additionalFeedback && (
          <div className="text-base text-gray-700 leading-relaxed">
            <p dangerouslySetInnerHTML={{ __html: review.additionalFeedback }}></p>
          </div>
        )}
      </div>
    </li>
  )
}

//-----------------------------------------------------------------------------
// MAIN COMPONENT
//-----------------------------------------------------------------------------

const AgencyDetail = (): JSX.Element => {
  const { permalink } = useParams<{ permalink?: string }>()
  const [agency, setAgency] = useState<Agency>()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>()
  const { showSuccessToast } = useAppToast()
  const history = useHistory()
  const location = useLocation()
  const { isAuthenticated, user } = useAuth0()
  const { isTokenReady } = useAuthToken()
  const [claimRefreshKey, setClaimRefreshKey] = useState(0)

  const agencyService = new AgencyService()

  const myPageEntry = useMyPageEntry(agency?.id ?? 0, 'agency', isAuthenticated, isTokenReady, claimRefreshKey)

  const viewerRole = usePageViewerRole({
    myPageEntry: myPageEntry ?? null,
    pageClaimStatus: agency?.claimStatus,
    pageId: agency?.id ?? 0,
    pageType: 'agency',
  })
  const showVerifiedBadge = shouldShowVerifiedBadge(viewerRole, 'agency')

  const fetchAgencyData = async () => {
    let searchOneOptions: {
      id?: number
      permalink?: string
    } = {
      permalink: permalink || '',
    }

    // check if the permalink in the url is a number indicating they are actually passing the id
    if (permalink && !isNaN(permalink as unknown as number)) {
      searchOneOptions = {
        id: parseInt(permalink),
      }
    }

    setIsLoading(true)

    return agencyService
      .searchOne(searchOneOptions)
      .then((resultAgency) => {
        setIsLoading(false)
        setAgency({
          ...resultAgency,
          claimStatus: resultAgency.claimStatus,
          image: resultAgency.image || AGENCY_PLACEHOLDER_IMAGE,
        })
      })
      .catch((err: Error & { status?: number }) => {
        if (err.status === 404) {
          history.push('/404')
        } else {
          setError(err.message)
          setIsLoading(false)
        }
      })
  }

  useEffect(() => {
    fetchAgencyData()
  }, [permalink])

  useEffect(() => {
    const scrollToReviewId = (location.state as DetailPageLocationState | undefined)?.scrollToReviewId
    if (!scrollToReviewId || isLoading || !agency) {
      return
    }

    const timer = window.setTimeout(() => {
      applyScrollToReviewFromLocationState(scrollToReviewId, agency.reviews)
      history.replace({ pathname: location.pathname, search: location.search, state: {} })
    }, 100)

    return () => window.clearTimeout(timer)
  }, [agency, history, isLoading, location.pathname, location.search, location.state])

  // Handle claim status change
  const handleClaimStatusChange = async () => {
    setClaimRefreshKey((k) => k + 1)
    await fetchAgencyData()
    showSuccessToast('Claim request submitted successfully! Our team will review it shortly.')
  }

  const handleShare = async () => {
    if (!agency) return

    const reviewCount = agency.reviewCount || 0
    const reviewText = reviewCount === 1 ? '' : 's'
    const rating = Number(agency.rating || 0)
    const isMobileDevice = isPlatform('android') || isPlatform('ios')
    const webBaseUrl = process.env.REACT_APP_WEB_URL || 'https://scrubstr.com'
    const pathSegment = agency.permalink || String(agency.id)
    const shareUrl = isMobileDevice ? `${webBaseUrl}/agencies/${pathSegment}` : window.location.href

    const shareData = {
      text: `Check out reviews for ${agency.name} on ScrubStr - ${rating.toFixed(1)} stars based on ${reviewCount} review${reviewText}`,
      title: `${agency.name} - Agency Reviews`,
      url: shareUrl,
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData)
        showSuccessToast('Shared successfully!')
      } else {
        await navigator.clipboard.writeText(shareUrl)
        showSuccessToast('Link copied to clipboard!')
      }
    } catch (error) {
      console.error('Error sharing:', error)
    }
  }

  return (
    <div id="page-root" className="h-screen overflow-auto relative">
      <TopLogo />

      <div className="mt-6 px-4 max-w-xl lg:max-w-6xl mx-auto relative">
        <div className="mb-4">
          <BackButton onClick={() => navigateBackToDirectory(history, location)} />
        </div>
        {error && <ErrorView>{error}</ErrorView>}
        {isLoading && !error && (
          <div className="flex justify-center items-center py-20 min-h-[400px]">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && !error && agency && (
          <>
            <Helmet>
              <title>{agency.name}</title>
              <meta property="og:title" content={agency.name} />
              <meta
                property="og:image"
                content={`https://scrubstr-prod.s3.amazonaws.com/agencies/agency_${agency.id}.png`}
              />
            </Helmet>

            <div className="mb-6 rounded-2xl p-6 bg-aqua-mint">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h1 className="mb-1 text-2xl font-bold text-white">
                    {agency.name}
                    {showVerifiedBadge && (
                      <span className="ml-1.5 inline-flex align-middle">
                        <VerifiedPageCard />
                      </span>
                    )}
                  </h1>

                  {formatAgencyLocation(agency.address) && (
                    <p className="mt-1 text-base text-white/90">{formatAgencyLocation(agency.address)}</p>
                  )}

                  {Number(agency.rating || 0) > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <StarRating rating={Number(agency.rating)} size="md" />
                      <span className="text-base font-semibold text-white">{Number(agency.rating).toFixed(1)}</span>
                      <span className="text-sm text-white/90">
                        {agency.reviewCount} review{agency.reviewCount && agency.reviewCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to={`/review-agency/${agency.id}`}
                      className="inline-block rounded-xl bg-orange px-4 py-2 text-base font-semibold text-white transition-all hover:bg-opacity-90"
                    >
                      Write Review
                    </Link>
                    <button
                      type="button"
                      onClick={handleShare}
                      className="inline-flex items-center gap-2 rounded-xl border border-white px-4 py-2 text-base font-semibold text-white transition-all hover:bg-white/10"
                    >
                      Share
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-white/90">
                    {agency.website && (
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 flex-shrink-0 text-sky-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                          />
                        </svg>
                        <a
                          href={`${!agency.website.startsWith('http') ? 'https://' : ''}${agency.website}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-300 hover:underline"
                        >
                          {formatAgencyWebsiteLabel(agency.website)}
                        </a>
                      </div>
                    )}
                    {agency.phone && (
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 flex-shrink-0 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <a href={`tel:${agency.phone.replace(/\D/g, '')}`} className="hover:underline">
                          {agency.phone}
                        </a>
                      </div>
                    )}
                    {agency.address && (
                      <div className="flex items-start gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{agency.address}</span>
                      </div>
                    )}
                  </div>
                </div>

                <OrganizationLogo imageUrl={agency.image} alt={agency.name} placeholder={AGENCY_PLACEHOLDER_IMAGE} />
              </div>
            </div>

            <PageStatusSection
              role={viewerRole}
              organizationType="agency"
              organizationId={agency.id}
              organizationName={agency.name}
              claimStatus={agency.claimStatus}
              myPageEntry={myPageEntry ?? null}
              userEmail={user?.email}
              onClaimStatusChange={handleClaimStatusChange}
            />

            {/* Rating Breakdown Section */}
            {agency.ratingsSummary && agency.ratingsSummary.length > 0 && (
              <div className="mb-8 rounded-lg p-6 bg-aqua-mint">
                <h3 className="text-xl font-bold text-white mb-6">Rating Breakdown</h3>
                <div className="space-y-4">
                  {agency.ratingsSummary.map((x, i) => (
                    <AgencySummaryItem summary={x} key={i} />
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(agency.reviews) && agency.reviews.length > 0 && (
              <div className="mb-8 bg-gray-50 rounded-lg p-6">
                <h3 id="detail-page-reviews" className="text-2xl font-bold text-graphite-grey mb-6" tabIndex={-1}>
                  Reviews Received
                </h3>
                <ul className="space-y-4">
                  {agency.reviews.map((x) => (
                    <AgencyReviewItem review={x} key={x.id} />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-12">
        <Footer />
      </div>
    </div>
  )
}

export default AgencyDetail
